import { useMemo, useCallback } from 'react'
import { useAttachmentUploadState } from './useAttachmentUploadState'
import { useAttachmentOfflineStyle } from './useAttachmentOfflineStyle'

type AttachmentInfo = {
  id?: string
  asset_url?: string
  image_url?: string
  thumb_url?: string
  file_url?: string
}

export const useAttachmentStatus = (
  attachments: AttachmentInfo[] | undefined,
  isOnline: boolean,
  onReload: (id: string) => void,
) => {
  const { state, actions, failedUploadsRef } = useAttachmentUploadState()
  const { applyOfflineStyle, removeOfflineStyle } = useAttachmentOfflineStyle(onReload)

  const updateAttachmentStyles = useCallback(() => {
    if (!attachments?.length) return

    const attachmentList = document.querySelector('.str-chat__attachment-preview-list')
    if (!attachmentList) return

    attachments.forEach((attachment, index) => {
      const attachmentId = attachment.id || `attachment-${index}`
      const hasUrl = !!(attachment.asset_url || attachment.image_url || attachment.thumb_url || (attachment as any).file_url)
      const wasUploading = state.uploadingFileIds.has(attachmentId)
      const isFailed = failedUploadsRef.current.has(attachmentId)
      const wasJustAdded = !hasUrl && !wasUploading && !isFailed

      const shouldBeOffline = isFailed || (!hasUrl && wasUploading && !isOnline) || (wasJustAdded && !isOnline)

      applyOfflineStyle(attachment, index, shouldBeOffline)

      if (!shouldBeOffline && hasUrl && !failedUploadsRef.current.has(attachmentId)) {
        actions.removeUploadingFile(attachmentId)
      }
    })
  }, [attachments, isOnline, state.uploadingFileIds, failedUploadsRef, applyOfflineStyle, actions])

  const shouldShowFailedMessage = useMemo(() => {
    if (!isOnline) return state.hasFailedUploads

    const hasAttachmentsWithoutUrl = attachments?.some((att) => {
      const hasUrl = !!(att.asset_url || att.image_url || att.thumb_url || (att as any).file_url)
      return !hasUrl
    })

    if (hasAttachmentsWithoutUrl || state.uploadingFileIds.size > 0) {
      return false
    }

    const allAttachmentsHaveUrl = attachments?.every((att) => {
      const hasUrl = !!(att.asset_url || att.image_url || att.thumb_url || (att as any).file_url)
      return hasUrl
    })

    if (allAttachmentsHaveUrl && attachments && attachments.length > 0) {
      actions.clearFailed()
      return false
    }

    return state.hasFailedUploads
  }, [isOnline, attachments, state.hasFailedUploads, state.uploadingFileIds.size, actions])

  return {
    uploadState: state,
    uploadActions: actions,
    updateAttachmentStyles,
    shouldShowFailedMessage,
    failedUploadsRef,
  }
}

